import type { JSX, ComponentPropsWithoutRef, ReactNode } from 'react';
import { styled } from 'styled-components';
import { useId } from 'react';
import { isNotNil } from '@wistia/type-guards';
import { ScreenReaderOnly } from '../ScreenReaderOnly';

export type MeterSegment = {
  /**
   * The percentage value this segment represents (0-100)
   */
  value: number;
  /**
   * The color for this segment using design token names (e.g., 'blue-9', 'green-9', 'red-9')
   */
  color: string;
  /**
   * Optional label for this segment shown in the key. Can be a string or React node for rich formatting.
   */
  label?: ReactNode;
};

const MeterWrapper = styled.div`
  --meter-height: 16px;

  display: flex;
  flex-direction: column;
  gap: var(--wui-space-02);
`;

const MeterLabelContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

const MeterLabel = styled.div`
  font-family: var(--wui-typography-heading-5-family);
  line-height: var(--wui-typography-heading-5-line-height);
  font-size: var(--wui-typography-heading-5-size);
  font-weight: var(--wui-typography-heading-5-weight);
`;

const MeterLabelMeta = styled.div`
  font-family: var(--wui-typography-heading-5-family);
  line-height: var(--wui-typography-heading-5-line-height);
  font-size: var(--wui-typography-heading-5-size);
  font-weight: var(--wui-typography-heading-5-weight);
`;

const MeterBarContainer = styled.div`
  position: relative;
  overflow: hidden;
  background-color: var(--wui-color-bg-surface-secondary);
  border-radius: var(--wui-border-radius-rounded);
  width: 100%;
  height: var(--meter-height);
  transform: translateZ(0);
`;

const MeterSegmentBar = styled.div<{
  $color: string;
  $width: number;
  $offset: number;
}>`
  position: absolute;
  top: 0;
  left: ${({ $offset }) => $offset}%;
  width: ${({ $width }) => $width}%;
  height: 100%;
  background-color: ${({ $color }) => `var(--wui-${$color})`};

  &:first-child {
    border-top-left-radius: var(--wui-border-radius-rounded);
    border-bottom-left-radius: var(--wui-border-radius-rounded);
  }

  &:last-child {
    border-top-right-radius: var(--wui-border-radius-rounded);
    border-bottom-right-radius: var(--wui-border-radius-rounded);
  }
`;

const MeterDescription = styled.div`
  line-height: var(--wui-typography-label-3-line-height);
  font-size: var(--wui-typography-label-3-size);
  font-weight: var(--wui-typography-label-3-weight);
  color: var(--wui-color-text-secondary);
`;

const MeterKey = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--wui-space-03);
`;

const MeterKeyItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--wui-space-01);
`;

const MeterKeyColorIndicator = styled.div<{
  $color: string;
}>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ $color }) => `var(--wui-${$color})`};
  flex-shrink: 0;
`;

const MeterKeyLabel = styled.span`
  line-height: var(--wui-typography-label-3-line-height);
  font-size: var(--wui-typography-label-3-size);
  font-weight: var(--wui-typography-label-3-weight);
  color: var(--wui-color-text-secondary);
`;

export type MeterProps = ComponentPropsWithoutRef<'div'> & {
  /**
   * Array of segments to display in the meter
   */
  segments: MeterSegment[];
  /**
   * Optional label displayed above the meter bar
   */
  label?: ReactNode;
  /**
   * Optional meta information displayed to the right of the label (e.g., "45 / 50", "100 / 200GB")
   */
  labelMeta?: ReactNode;
  /**
   * Optional description displayed underneath the meter bar
   */
  description?: ReactNode;
  /**
   * Whether to hide the key below the meter bar with segment labels and colors
   */
  hideKey?: boolean;
  /**
   * Optional accessible description for screen readers. If not provided, one will be generated from the segments.
   */
  'aria-label'?: string;
  /**
   * The maximum value that the meter represents (defaults to 100)
   */
  max?: number;
};

// helper to convert ReactNode to string for accessibility
// TODO: is this added complexity worth it?
const nodeToString = (node: ReactNode): string => {
  if (typeof node === 'string') {
    return node;
  }
  if (typeof node === 'number') {
    return String(node);
  }
  if (node == null || typeof node === 'boolean') {
    return '';
  }
  if (Array.isArray(node)) {
    return node.map(nodeToString).join('');
  }
  // React elements, extract text from children
  if (typeof node === 'object' && 'props' in node) {
    const element = node as { props?: { children?: ReactNode } };
    if (element.props?.children != null) {
      return nodeToString(element.props.children);
    }
  }
  // fallback for other object types - return empty string to avoid [object Object]
  return '';
};

/**
 * A meter component that visually represents one to many segments as portions of a whole
 */
export const Meter = ({
  segments,
  label,
  labelMeta,
  description,
  hideKey = false,
  max = 100,
  'aria-label': ariaLabel,
  ...props
}: MeterProps): JSX.Element => {
  const labelId = useId();
  const descriptionId = useId();

  // cumulative offsets for positioning segments, normalized to max prop
  const segmentsWithOffsets = segments.reduce<
    (MeterSegment & { offset: number; widthPercent: number })[]
  >((acc, segment) => {
    const offset = acc.reduce((sum, prev) => sum + prev.widthPercent, 0);
    const widthPercent = (segment.value / max) * 100;
    acc.push({ ...segment, offset, widthPercent });
    return acc;
  }, []);

  // only show segments with labels in the key
  const keySegments = segmentsWithOffsets.filter(
    (
      segment,
    ): segment is MeterSegment & { offset: number; widthPercent: number; label: ReactNode } =>
      isNotNil(segment.label),
  );

  // total value for screen readers
  const totalValue = segments.reduce((sum, segment) => sum + segment.value, 0);

  const generateAriaDescription = (): string => {
    const segmentDescriptions = segments
      .filter((segment): segment is MeterSegment & { label: ReactNode } => isNotNil(segment.label))
      .map((segment) => `${nodeToString(segment.label)}: ${segment.value}%`)
      .join(', ');

    const totalDescription = `Total: ${totalValue} out of ${max}`;

    return segmentDescriptions ? `${segmentDescriptions}. ${totalDescription}` : totalDescription;
  };

  // determine appropriate ARIA label
  const effectiveAriaLabel = ariaLabel ?? (isNotNil(label) ? nodeToString(label) : 'Data meter');

  return (
    <MeterWrapper {...props}>
      <ScreenReaderOnly
        id={descriptionId}
        text={generateAriaDescription()}
      />

      {isNotNil(label) || isNotNil(labelMeta) ? (
        <MeterLabelContainer id={labelId}>
          {isNotNil(label) ? <MeterLabel>{label}</MeterLabel> : null}
          {isNotNil(labelMeta) ? <MeterLabelMeta>{labelMeta}</MeterLabelMeta> : null}
        </MeterLabelContainer>
      ) : null}

      <MeterBarContainer
        aria-describedby={descriptionId}
        aria-label={effectiveAriaLabel}
        aria-labelledby={isNotNil(label) || isNotNil(labelMeta) ? labelId : undefined}
        aria-valuemax={max}
        aria-valuemin={0}
        aria-valuenow={totalValue}
        role="meter"
      >
        {segmentsWithOffsets.map((segment) => (
          <MeterSegmentBar
            key={`${segment.color}-${segment.value}-${segment.offset}`}
            $color={segment.color}
            $offset={segment.offset}
            $width={segment.widthPercent}
            aria-hidden="true" // hide segments from screen readers since we provide a description
          />
        ))}
      </MeterBarContainer>

      {isNotNil(description) ? <MeterDescription>{description}</MeterDescription> : null}

      {!hideKey && keySegments.length > 0 ? (
        <MeterKey
          aria-label="Meter legend"
          role="list"
        >
          {keySegments.map((segment) => (
            <MeterKeyItem
              key={`${segment.color}-${segment.value}-${segment.offset}-${nodeToString(segment.label)}`}
              role="listitem"
            >
              <MeterKeyColorIndicator
                $color={segment.color}
                aria-hidden="true"
              />
              <MeterKeyLabel>{segment.label}</MeterKeyLabel>
            </MeterKeyItem>
          ))}
        </MeterKey>
      ) : null}
    </MeterWrapper>
  );
};

Meter.displayName = 'Meter';
